import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChatsCircle, Question, WarningCircle, Flag, Microphone, CaretRight, Target, ArrowClockwise } from "@phosphor-icons/react";
import { AdminHeader, LiveIndicator } from "@/components/admin/parts";
import { OutcomeChip } from "@/components/admin/OutcomeChip";
import { adminApi } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { RangeControls } from "@/components/admin/dashboard/RangeControls";
import { UsageChart } from "@/components/admin/dashboard/UsageChart";
import { PeakHeatmap } from "@/components/admin/dashboard/PeakHeatmap";
import { ResolutionCard } from "@/components/admin/dashboard/ResolutionCard";
import { TicketsOverview, DiscrepanciesOverview } from "@/components/admin/dashboard/AttentionPanels";
import { Panel, presetRange, rangeLabel } from "@/components/admin/dashboard/dashUtils";

function Kpi({ icon: Icon, label, value, sub, warn, accent, testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl border p-5 transition-colors ${accent ? "bg-[#73bf44]/[0.06] border-[#73bf44]/30" : "bg-[#0F0F0F] border-neutral-800 hover:border-neutral-700"}`}>
      <Icon size={20} weight="duotone" className={warn ? "text-amber-400" : "text-[#73bf44]"} />
      <div className="font-heading text-3xl font-bold text-white mt-3 tracking-tight" data-testid={`${testid}-value`}>{value}</div>
      <div className="text-xs text-neutral-500 mt-1">{label}</div>
      {sub && <div className="text-[0.65rem] text-neutral-600 mt-0.5">{sub}</div>}
    </div>
  );
}

function useDashboard(range, granularity) {
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loadingA, setLoadingA] = useState(true);
  const [error, setError] = useState(false);
  const loadAll = useCallback(() => {
    adminApi.get("/admin/stats").then(({ data }) => { setStats(data); setError(false); }).catch(() => setError(true));
    adminApi.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => {});
    const params = range.preset === "all" ? { all_time: true, granularity: "day" } : { start: range.start, end: range.end, granularity };
    adminApi.get("/admin/analytics", { params })
      .then(({ data }) => setAnalytics(data)).catch(() => {}).finally(() => setLoadingA(false));
  }, [range, granularity]);
  useEffect(() => { setLoadingA(true); loadAll(); }, [loadAll]);
  usePolling(loadAll, 15000);
  return { stats, overview, analytics, loadingA, error, reload: loadAll };
}

function RecentConversations({ rows }) {
  const navigate = useNavigate();
  return (
    <Panel title="Recent conversations" className="lg:col-span-2" testid="recent-conversations"
      aside={<button onClick={() => navigate("/admin/conversations")} className="text-xs text-[#73bf44] hover:underline" data-testid="dashboard-view-all-conversations">View all</button>}>
      {rows?.length ? (
        <div className="space-y-1">
          {rows.map((c) => (
            <button key={c.id} onClick={() => navigate(`/admin/conversations/${c.id}`)} className="w-full flex items-center gap-3 text-left rounded-lg px-3 py-2.5 hover:bg-white/[0.03] transition-colors group">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-neutral-200 truncate">{c.last_message || "New conversation"}</div>
                <div className="text-xs text-neutral-600">{c.chassis_number ? `${c.model || ""} · ${c.chassis_number}` : "No caravan linked"}</div>
              </div>
              {c.has_voice && <Microphone size={13} className="text-sky-300" weight="fill" />}
              <OutcomeChip outcome={c.outcome} reason={c.outcome_reason} />
              <CaretRight size={14} className="text-neutral-600 group-hover:text-white" />
            </button>
          ))}
        </div>
      ) : <p className="text-sm text-neutral-600">No conversations yet.</p>}
    </Panel>
  );
}

function KnowledgePanel({ stats }) {
  const rows = [["Caravans in database", stats.caravans], ["Documents indexed", stats.documents], ["User manuals", stats.manuals],
    ["Knowledge entries", stats.knowledge], ["Video tutorials", stats.videos], ["Admin corrections", stats.feedback]];
  return (
    <Panel title="Knowledge environment" testid="knowledge-panel">
      <div className="space-y-2.5 text-sm">
        {rows.map(([l, v]) => (
          <div key={l} className="flex items-center justify-between border-b border-neutral-800/60 pb-2"><span className="text-neutral-500">{l}</span><span className="text-white font-medium tabular-nums">{v}</span></div>
        ))}
      </div>
    </Panel>
  );
}

export default function Dashboard() {
  const [range, setRange] = useState(() => presetRange("all"));
  const [granularity, setGranularity] = useState("day");
  const { stats, overview, analytics, loadingA, error, reload } = useDashboard(range, granularity);
  const tot = analytics?.totals;

  if (error && !stats) {
    return (
      <div>
        <AdminHeader title="Dashboard" />
        <div className="p-8">
          <div className="border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-4" data-testid="dashboard-error">
            <WarningCircle size={36} className="text-amber-400" weight="duotone" />
            <div className="text-neutral-300">Couldn't load the dashboard. Please check your connection and try again.</div>
            <button onClick={reload} data-testid="dashboard-retry" className="inline-flex items-center gap-2 bg-[#73bf44] text-[#050505] rounded-full font-semibold px-5 py-2.5 text-sm hover:bg-[#5fa338]"><ArrowClockwise size={15} /> Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminHeader title="Dashboard" subtitle="Nick's activity, the support queue and configuration discrepancies" actions={<LiveIndicator />} />
      <div className="p-8 space-y-6 max-w-[1700px]">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-heading font-semibold text-white tracking-tight">Overview</h2>
            <p className="text-xs text-neutral-500" data-testid="dashboard-range-label">{range.preset === "all" ? `All time${analytics?.start ? ` · since ${rangeLabel({ start: analytics.start, end: analytics.start })}` : ""}` : rangeLabel(range)} · Brisbane time</p>
          </div>
          <RangeControls range={range} onRange={setRange} granularity={granularity} onGranularity={setGranularity} />
        </div>

        <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="dashboard-top-row">
          <UsageChart data={analytics} loading={loadingA} />
          <ResolutionCard data={analytics} onReviewed={reload} />
          <TicketsOverview data={overview} />
        </div>

        <h2 className="text-lg font-heading font-semibold text-white tracking-tight pt-2" data-testid="customer-activity-heading">Customer activity</h2>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Kpi testid="kpi-conversations" icon={ChatsCircle} label="Chats in range" value={tot?.conversations ?? "—"} sub={stats && `${stats.conversations} all time`} accent />
          <Kpi testid="kpi-questions" icon={Question} label="Questions asked" value={tot?.questions ?? "—"} sub={stats && `${stats.questions} all time`} />
          <Kpi testid="kpi-voice-share" icon={Microphone} label="Voice share" value={tot ? `${tot.voice_share}%` : "—"} sub={tot && `${tot.voice} voice · ${tot.text} text`} />
          <Kpi testid="kpi-resolution" icon={Target} label="Resolution rate" value={analytics?.resolution?.rate == null ? "—" : `${analytics.resolution.rate}%`} sub={analytics && `${analytics.resolution.judged} chats judged`} />
          <Kpi testid="kpi-low-confidence" icon={stats?.flagged ? Flag : WarningCircle} label="Low confidence · flagged" value={stats ? `${stats.low_confidence} · ${stats.flagged}` : "—"} warn={!!(stats?.low_confidence || stats?.flagged)} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2"><PeakHeatmap data={analytics} /></div>
          {stats && <KnowledgePanel stats={stats} />}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <RecentConversations rows={stats?.recent} />
          <DiscrepanciesOverview data={overview} />
        </div>
      </div>
    </div>
  );
}
